import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

export default function FAQ() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Navigation />
      <div className="container mx-auto px-4 py-8 pt-24">

        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center mb-4">
              <HelpCircle className="h-12 w-12 text-primary mr-3" />
              <h1 className="text-4xl font-bold">Frequently Asked Questions</h1>
            </div>
            <p className="text-xl text-muted-foreground">
              Find answers to common questions about MeetHub
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>General Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger>What is MeetHub?</AccordionTrigger>
                  <AccordionContent>
                    MeetHub is a professional video conferencing platform designed for teams, educators, and communities. It offers high-quality video calls, screen sharing, recording, chat, and many other collaboration features with a focus on security and privacy.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger>Is MeetHub free to use?</AccordionTrigger>
                  <AccordionContent>
                    MeetHub offers both free and premium plans. The free plan includes basic video conferencing features for small teams. Premium plans offer advanced features like extended meeting duration, more participants, and additional collaboration tools.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger>How many participants can join a meeting?</AccordionTrigger>
                  <AccordionContent>
                    The number of participants depends on your plan. Free accounts support up to 100 participants, while premium plans can accommodate up to 1000 participants or more depending on your subscription tier.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger>Do I need to download any software?</AccordionTrigger>
                  <AccordionContent>
                    No download required! MeetHub works directly in your web browser. We support all modern browsers including Chrome, Firefox, Safari, and Edge. Simply click a meeting link and you're ready to join.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Security & Privacy</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="security-1">
                  <AccordionTrigger>Is MeetHub secure?</AccordionTrigger>
                  <AccordionContent>
                    Yes! MeetHub uses end-to-end encryption for all meetings, is GDPR and CCPA compliant, and follows industry-leading security practices. Your data is protected with enterprise-grade security measures including encrypted storage and secure data transmission.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="security-2">
                  <AccordionTrigger>Are my meetings encrypted?</AccordionTrigger>
                  <AccordionContent>
                    Yes, all MeetHub meetings use end-to-end encryption. This means your video, audio, and chat messages are encrypted before leaving your device and can only be decrypted by participants in the meeting.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="security-3">
                  <AccordionTrigger>How do you handle my data?</AccordionTrigger>
                  <AccordionContent>
                    We take data privacy seriously. We never sell your data, and we only collect information necessary to provide our services. Meeting recordings are stored securely and can be deleted at any time. For more details, please review our Privacy Policy.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Features & Technical</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="features-1">
                  <AccordionTrigger>Can I record meetings?</AccordionTrigger>
                  <AccordionContent>
                    Yes! Meeting hosts can record sessions with participant consent. Recordings are stored securely in the cloud and can be shared with participants or downloaded. All participants are notified when recording starts.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="features-2">
                  <AccordionTrigger>Does MeetHub support screen sharing?</AccordionTrigger>
                  <AccordionContent>
                    Yes, MeetHub supports full-screen sharing, application window sharing, and even specific browser tab sharing. You can also share your screen with audio for presentations and demos.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="features-3">
                  <AccordionTrigger>Can guests join without an account?</AccordionTrigger>
                  <AccordionContent>
                    Absolutely! Guests can join meetings without creating an account. They simply need the meeting link or code and can enter their name to join. Account creation is only required for hosting meetings.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="features-4">
                  <AccordionTrigger>What are breakout rooms?</AccordionTrigger>
                  <AccordionContent>
                    Breakout rooms allow hosts to split participants into smaller groups for discussions or activities. It's perfect for workshops, training sessions, and collaborative meetings. Hosts can move between rooms and broadcast messages to all rooms.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Troubleshooting</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="trouble-1">
                  <AccordionTrigger>My camera/microphone isn't working</AccordionTrigger>
                  <AccordionContent>
                    First, check that your browser has permission to access your camera and microphone. In your browser settings, ensure MeetHub is allowed to use these devices. Also, make sure no other application is currently using your camera or microphone.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="trouble-2">
                  <AccordionTrigger>The video quality is poor</AccordionTrigger>
                  <AccordionContent>
                    Video quality depends on your internet connection. For best results, we recommend a minimum of 3 Mbps upload/download speed. Try closing other applications using bandwidth, moving closer to your WiFi router, or using a wired connection if possible.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="trouble-3">
                  <AccordionTrigger>I can't hear other participants</AccordionTrigger>
                  <AccordionContent>
                    Check that your device volume is turned up and not muted. Also verify that the correct audio output device is selected in MeetHub's settings. If you're using headphones, make sure they're properly connected.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Still have questions?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Can't find the answer you're looking for? Our support team is here to help!
              </p>
              <Button onClick={() => navigate("/contact")}>
                Contact Support
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
